import * as React from 'react';
import { HashRouter as Router, Redirect, Route, RouteComponentProps } from 'react-router-dom';
import { Watchlist } from 'watchlist';
import './App.css';
import { AppContext } from './AppContextType';
import { FinderProps } from './Components/Finder/AbstractFinder';
import { AmplifierFinder } from './Components/Finder/Amplifier/AmplifierFinder';
import { ElectronicsFinder } from './Components/Finder/Electronics/ElectronicsFinder';
import { ForceSensorFinder } from './Components/Finder/ForceSensor/ForceSensorFinder';
import { LinearActuatorFinder } from './Components/Finder/LinearActuator/LinearActuatorFinder';
import { MaterialSensorFinder } from './Components/Finder/MaterialSensor/MaterialSensorFinder';
import { MeasuringRollerFinder } from './Components/Finder/MeasuringRoller/MeasuringRollerFinder';
import { SteeringFrameFinder } from './Components/Finder/SteeringFrame/SteeringFrameFinder';
import { TelemetrySystemFinder } from './Components/Finder/TelemetrySystem/TelemetrySystemFinder';
import { TensionAccessoriesFinder } from './Components/Finder/TensionAccessories/TensionAccessoriesFinder';
import { TensionControllerFinder } from './Components/Finder/TensionController/TensionControllerFinder';
import { WebGuideAccessoriesFinder } from './Components/Finder/WebGuideAccessories/WebGuideAccessoriesFinder';
import { WebGuideControllerFinder } from './Components/Finder/WebGuideController/WebGuideControllerFinder';
import { WatchlistController } from './Components/Watchlist/WatchlistController';
import { Groups } from './Enum/Groups';
import { HandlerContextProvider } from './HandlerContextProvider';
import { AppState, MainHandler } from './MainHandler';
import { ProductInterface } from './Model/Product';
import { GroupUri } from './Service/UriBuilder';
import { ServiceLocator } from './ServiceLocator';

export interface AppProps<T extends ProductInterface> {
    serviceLocator: ServiceLocator;
    handler: MainHandler<T>;
}

interface AppStateLocal<T> extends AppState<T> {
    error?: Error | string
}

export class App<T extends ProductInterface> extends React.Component<AppProps<T>, AppStateLocal<T>> {
    constructor(props: AppProps<T>, context: AppContext<T>) {
        super(props, context);
        this.state = this.props.handler.state;
    }

    public render() {
        const serviceLocator = this.props.serviceLocator;
        const handler = this.props.handler;

        /* tslint:disable:jsx-no-lambda */
        return <Router>
            <HandlerContextProvider handler={handler} serviceLocator={serviceLocator}>
                <div id="pf-app">
                    <Route render={() => <Redirect to={'/force-sensor'}/>}
                           path="/"
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.SteeringFrame, routeProps)}
                           path={GroupUri.SteeringFrame}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.LinearActuator, routeProps)}
                           path={GroupUri.LinearActuator}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.ForceSensor, routeProps)}
                           path={GroupUri.ForceSensor}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.TensionController, routeProps)}
                           path={GroupUri.TensionController}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.Amplifier, routeProps)}
                           path={GroupUri.Amplifier}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.WebGuideController, routeProps)}
                           path={GroupUri.WebGuideController}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.MaterialSensor, routeProps)}
                           path={GroupUri.MaterialSensor}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.TensionAccessories, routeProps)}
                           path={GroupUri.TensionAccessories}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.WebGuideAccessories, routeProps)}
                           path={GroupUri.WebGuideAccessories}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.TelemetrySystem, routeProps)}
                           path={GroupUri.TelemetrySystem}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.MeasuringRoller, routeProps)}
                           path={GroupUri.MeasuringRoller}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderFinder(Groups.Electronics, routeProps)}
                           path={GroupUri.Electronics}
                           exact={true}/>
                    <Route render={(routeProps) => this.renderAll(routeProps)}
                           path="/all"
                           exact={true}/>
                    <Route render={(routeProps) => this.renderWatchlist(routeProps)}
                           path="/watchlist"
                           exact={true}/>
                </div>
            </HandlerContextProvider>
        </Router>;
    }

    private renderFinder(group: Groups, routeProps: RouteComponentProps): JSX.Element {
        const searchParams = new URLSearchParams(routeProps.location.search);
        const props: FinderProps = {
            serviceLocator: this.props.serviceLocator,
            history: routeProps.history,
            searchParams
        };
        switch (group) {
            case Groups.SteeringFrame:
                return <SteeringFrameFinder {...routeProps} {...props}/>;
            case Groups.LinearActuator:
                return <LinearActuatorFinder {...routeProps} {...props}/>;
            case Groups.WebGuideController:
                return <WebGuideControllerFinder {...routeProps} {...props}/>;
            case Groups.TensionController:
                return <TensionControllerFinder {...routeProps} {...props}/>;
            case Groups.ForceSensor:
                return <ForceSensorFinder {...routeProps} {...props}/>;
            case Groups.Amplifier:
                return <AmplifierFinder {...routeProps} {...props}/>;
            case Groups.MaterialSensor:
                return <MaterialSensorFinder {...routeProps} {...props}/>;
            case Groups.TensionAccessories:
                return <TensionAccessoriesFinder {...routeProps} {...props}/>;
            case Groups.WebGuideAccessories:
                return <WebGuideAccessoriesFinder {...routeProps} {...props}/>;
            case Groups.TelemetrySystem:
                return <TelemetrySystemFinder {...routeProps} {...props}/>;
            case Groups.MeasuringRoller:
                return <MeasuringRollerFinder {...routeProps} {...props}/>;
            case Groups.Electronics:
                return <ElectronicsFinder {...routeProps} {...props}/>;
        }
    }

    private renderAll(routeProps: RouteComponentProps<any>) {
        return <>
            {this.renderFinder(Groups.SteeringFrame, routeProps)}
            {this.renderFinder(Groups.LinearActuator, routeProps)}
            {this.renderFinder(Groups.ForceSensor, routeProps)}
            {this.renderFinder(Groups.TensionController, routeProps)}
            {this.renderFinder(Groups.Amplifier, routeProps)}
            {this.renderFinder(Groups.WebGuideController, routeProps)}
            {this.renderFinder(Groups.MaterialSensor, routeProps)}
            {this.renderFinder(Groups.TensionAccessories, routeProps)}
            {this.renderFinder(Groups.WebGuideAccessories, routeProps)}
            {this.renderFinder(Groups.TelemetrySystem, routeProps)}
        </>;
    }

    private renderWatchlist(routeProps: RouteComponentProps<any>) {
        const watchlist: Watchlist = this.props.serviceLocator.get('watchlist');

        return <WatchlistController {...routeProps} watchlist={watchlist}/>;
    }
}
